import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { authClient } from "@/lib/auth-client";
import { useAuth } from "@/lib/auth-provider";
import { SocialAuthButtons } from "@/components/social-auth-buttons";

export default function SignupPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const result = await authClient.signUp.email({ email, password, name });
      if (result.error) {
        if (result.error.message?.includes("ALREADY_EXISTS") || result.error.message?.includes("exists")) {
          setError("An account with this email already exists. Try signing in.");
        } else {
          setError(result.error.message ?? "Signup failed. Please try again.");
        }
      } else {
        const refreshed = await refresh();

        if (refreshed.status === "authenticated" && refreshed.bootstrapState === "ready") {
          navigate("/onboarding", { replace: true });
          return;
        }

        if (refreshed.status === "authenticated" && refreshed.bootstrapState === "missing-organization") {
          setError("Your account is ready, but your workspace is still finishing setup. Wait a few seconds, then sign in again to continue.");
          return;
        }

        if (refreshed.status === "authenticated" && refreshed.bootstrapState === "failed") {
          setError(refreshed.message ?? "Your account was created, but we could not finish opening your workspace. Sign in again to retry.");
          return;
        }

        if (refreshed.status === "unauthenticated") {
          setError("Your account was created, but we could not keep you signed in. Sign in to continue.");
          return;
        }

        setError(refreshed.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Signup failed.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left: branding panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-brand text-white flex-col justify-between p-12">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">MFM Effect</h2>
          <p className="mt-1 text-white/70 text-sm">Market. Funnel. Monetize.</p>
        </div>
        <div className="space-y-6">
          <h3 className="text-2xl font-semibold">Start for free</h3>
          <ul className="space-y-3 text-[var(--color-primary)]/20">
            <li className="flex items-center gap-3"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--color-primary)] text-xs">1</span> 500 contacts included</li>
            <li className="flex items-center gap-3"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--color-primary)] text-xs">2</span> CRM, email, and pages</li>
            <li className="flex items-center gap-3"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--color-primary)] text-xs">3</span> 3 workflow automations</li>
            <li className="flex items-center gap-3"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--color-primary)] text-xs">4</span> No credit card required</li>
          </ul>
        </div>
        <p className="text-xs text-[var(--color-primary)]/50">Upgrade anytime. Plans from EUR 89/mo.</p>
      </div>

      {/* Right: signup form */}
      <div className="flex flex-1 items-center justify-center px-6 py-12 bg-background">
        <div className="w-full max-w-sm space-y-6">
          <div className="lg:hidden text-center mb-2">
            <h2 className="text-xl font-bold text-primary">MFM Effect</h2>
          </div>

          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground">Create your account</h1>
            <p className="mt-1 text-sm text-muted-foreground">Create your workspace, answer a few setup questions, and land in your first draft.</p>
          </div>

          <SocialAuthButtons mode="signup" onError={setError} />

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Or create with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="rounded-lg bg-[var(--color-destructive)]/5 border border-[var(--color-destructive)]/20 p-3 text-sm text-[var(--color-destructive)] dark:bg-[var(--color-destructive)]/20 dark:border-[var(--color-destructive)] dark:text-[var(--color-destructive)]/80">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-foreground">Full name</label>
              <input
                id="name"
                type="text"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                placeholder="Your full name"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground">Email</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground">Password</label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                placeholder="Min 8 characters"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {isLoading ? "Creating account..." : "Create free account"}
            </button>

            <p className="text-xs text-center text-muted-foreground">
              By creating an account, you agree to our{" "}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">Terms of Service</a>
              {" "}and{" "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">Privacy Policy</a>.
            </p>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-primary hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
